import { useEffect, useRef, useCallback } from "react";
import { BACKEND_URL } from "@/lib/api";

/**
 * useLiveWS — production-grade WebSocket hook for live show feeds.
 *
 * Features:
 *  - Auto-reconnect with exponential backoff (0.5s → 1s → 2s → 4s → max 5s)
 *  - Client-side ping every 20s to keep the connection alive through proxies
 *  - Server-side `heartbeat` frames are absorbed silently (server sends every 25s)
 *  - HTTP fallback: when no message received for `fallbackEveryMs`, calls fallbackFetch()
 *    and feeds the result back to onMessage as a synthetic 'snapshot'.
 *  - Tracks connection state via onStatus(connected: bool).
 *
 * Usage:
 *   useLiveWS({
 *     path: "/api/daily/ws",
 *     fallbackPath: "/api/daily/state",
 *     onMessage: (msg) => ...,
 *     onStatus: (connected) => ...,
 *   });
 */
export function useLiveWS({
  path,
  fallbackPath,
  onMessage,
  onStatus,
  pingEveryMs = 20000,
  fallbackEveryMs = 7000,
}) {
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const lastMessageAtRef = useRef(0);
  const closedByUserRef = useRef(false);
  const onMessageRef = useRef(onMessage);
  const onStatusRef = useRef(onStatus);

  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { onStatusRef.current = onStatus; }, [onStatus]);

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    if (pingTimerRef.current) clearInterval(pingTimerRef.current);
    if (fallbackTimerRef.current) clearInterval(fallbackTimerRef.current);
    reconnectTimerRef.current = null;
    pingTimerRef.current = null;
    fallbackTimerRef.current = null;
  }, []);

  const connect = useCallback(() => {
    closedByUserRef.current = false;
    const token = localStorage.getItem("nk_session_token");
    const wsProto = BACKEND_URL.startsWith("https") ? "wss" : "ws";
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, "");
    const url = `${wsProto}://${wsHost}${path}?token=${encodeURIComponent(token || "")}`;
    let ws;
    try { ws = new WebSocket(url); }
    catch (e) { scheduleReconnect(); return; }
    wsRef.current = ws;

    ws.onopen = () => {
      lastMessageAtRef.current = Date.now();
      onStatusRef.current?.(true);
      // Only reset the backoff counter after the connection has been stable for 5s,
      // so a flapping connection escalates the backoff instead of resetting to 0.5s.
      const stabilityTimer = setTimeout(() => {
        if (wsRef.current === ws && ws.readyState === WebSocket.OPEN) {
          reconnectAttemptsRef.current = 0;
        }
      }, 5000);
      // Periodic ping (keeps proxies happy + detects half-open sockets)
      pingTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.send(JSON.stringify({ type: "ping" })); } catch (_) { /* */ }
        }
      }, pingEveryMs);
      // Clean up stability timer if we close before it fires
      ws.addEventListener("close", () => clearTimeout(stabilityTimer), { once: true });
    };
    ws.onmessage = (ev) => {
      lastMessageAtRef.current = Date.now();
      try {
        const m = JSON.parse(ev.data);
        if (m.type === "heartbeat" || m.type === "pong") return;
        onMessageRef.current?.(m);
      } catch { /* ignore */ }
    };
    ws.onerror = () => { /* will trigger onclose */ };
    ws.onclose = (event) => {
      // Guard against stale socket: ignore onclose from any WS that's no longer the active one
      // (React StrictMode dev double-mount, or back-to-back reconnects).
      if (wsRef.current !== null && wsRef.current !== ws) return;
      onStatusRef.current?.(false);
      clearTimers();
      if (!closedByUserRef.current) scheduleReconnect();
    };
  }, [path, pingEveryMs, clearTimers]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) return;
    const attempt = Math.min(reconnectAttemptsRef.current, 5);
    const delay = Math.min(500 * Math.pow(2, attempt), 5000);
    reconnectAttemptsRef.current += 1;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connect();
    }, delay);
  }, [connect]);

  // HTTP fallback poller — kicks in when WS is silent
  useEffect(() => {
    if (!fallbackPath) return;
    fallbackTimerRef.current = setInterval(async () => {
      const silentFor = Date.now() - lastMessageAtRef.current;
      if (silentFor < fallbackEveryMs) return;
      try {
        const token = localStorage.getItem("nk_session_token");
        const r = await fetch(`${BACKEND_URL}${fallbackPath}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok) return;
        const data = await r.json();
        // Feed as synthetic snapshot
        onMessageRef.current?.({ type: "snapshot", data });
        lastMessageAtRef.current = Date.now();
      } catch (_) { /* offline — try again next tick */ }
    }, fallbackEveryMs);
    return () => clearInterval(fallbackTimerRef.current);
  }, [fallbackPath, fallbackEveryMs]);

  useEffect(() => {
    connect();
    return () => {
      closedByUserRef.current = true;
      clearTimers();
      try { wsRef.current?.close(); } catch (_) { /* */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendRaw = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify(payload)); } catch (_) { /* */ }
    }
  }, []);

  return { sendRaw };
}
